import { Spacing } from '@persona-id/design-tokens';
import { useTranslation } from 'react-i18next';
import { FONT_SIZES, MEDIA_QUERIES } from 'shared/styles/variables';
import styled, { css } from 'styled-components';

import { BASE } from 'design-system/variables/spacing';
import FrameTrigger from 'lib/clients/FrameTrigger';
import { sizeCss } from 'lib/inquiryStyles';
import { StepSelfieConfig } from 'lib/interfaces/StepsConfig';
import { TextComponentStyles } from 'lib/interfaces/ui-components';
import ThemedParagraph from 'verify/components/ThemedParagraph';
import { SelfieTriggerParams, SelfieTriggerType } from 'verify/scanners/SelfieScanner/client';

const BaseWrapper = styled('div')<{ styles?: TextComponentStyles }>`
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: space-around;

  padding-left: ${Spacing[6]};
  padding-right: ${Spacing[6]};

  ${({ styles }) => {
    const lineHeight = styles?.['line-height']?.base.base;
    if (lineHeight == null) return;

    /* Default to allowing 2 line of wrapping */
    return css`
      min-height: ${sizeCss({ ...lineHeight, value: lineHeight.value * 2 })};
    `;
  }};
`;

const Wrapper = styled(BaseWrapper)<{ color?: string }>`
  color: ${({ color, theme }) => color ?? theme.colorFont};
  font-size: ${FONT_SIZES.h3};
  height: ${BASE * 7.5}px;

  @media ${MEDIA_QUERIES.isMobile} {
    font-size: ${FONT_SIZES.h2};
    height: ${BASE * 12.5}px;
  }
`;

interface HintPromptProps {
  activeTrigger: FrameTrigger<
    SelfieTriggerType,
    SelfieTriggerParams,
    StepSelfieConfig['localizations']['capture-page']
  >;
  color?: string;
  styles?: TextComponentStyles;
  localizations?: StepSelfieConfig['localizations']['capture-page'];
}

export function HintPrompt({ activeTrigger, color, styles, localizations }: HintPromptProps) {
  const [t] = useTranslation();
  return (
    <BaseHintPrompt
      color={color}
      styles={styles}
      hintText={activeTrigger.getPrompt({ t, localizations })}
    />
  );
}
interface BaseHintPromptProps {
  color?: string;
  styles?: TextComponentStyles;
  hintText?: string;
}

export function BaseHintPrompt({ color, styles, hintText }: BaseHintPromptProps) {
  return (
    <>
      {styles != null ? (
        <BaseWrapper styles={styles}>
          <ThemedParagraph role='alert' styles={styles}>
            {hintText}
          </ThemedParagraph>
        </BaseWrapper>
      ) : (
        <Wrapper color={color}>
          <p role='alert'>{hintText}</p>
        </Wrapper>
      )}
    </>
  );
}
